import { z } from 'zod';
import { refersTo } from '../common/refers-to';

/** Bereich. Stored and on the wire in English; the German labels live in @immo/i18n. Order = label order. */
export const EMAIL_TEMPLATE_AREAS = ['BUYER', 'OWNER', 'GENERAL'] as const;
export const EmailTemplateAreaSchema = z.enum(EMAIL_TEMPLATE_AREAS);
export type EmailTemplateArea = z.infer<typeof EmailTemplateAreaSchema>;

/**
 * The fixed placeholder list. Keys are English tokens persisted inside the
 * stored markup; the German name is `common.email-templates.placeholders.{KEY}`
 * in @immo/i18n.
 */
export const EMAIL_TEMPLATE_PLACEHOLDERS = [
  'RECIPIENT_GREETING',
  'RECIPIENT_SALUTATION',
  'RECIPIENT_FIRST_NAME',
  'RECIPIENT_LAST_NAME',
  'PROPERTY_TITLE',
  'PROPERTY_LOCATION',
  'PROPERTY_ADDRESS',
  'PROPERTY_PRICE',
  'PROPERTY_NUMBER',
  'PROSPECT_OFFER',
  'PROSPECT_NOTARY_APPOINTMENT',
  'PROSPECT_PERSONAL_LINK',
  'SENDER_FIRST_NAME',
  'SENDER_LAST_NAME',
  'SENDER_PHONE',
  'SENDER_EMAIL',
  'COMPANY_NAME',
] as const;
export type EmailTemplatePlaceholder = (typeof EMAIL_TEMPLATE_PLACEHOLDERS)[number];

export const EMAIL_TEMPLATE_PLACEHOLDER_GROUPS = ['RECIPIENT', 'PROPERTY', 'PROSPECT', 'SENDER', 'COMPANY'] as const;
export type EmailTemplatePlaceholderGroup = (typeof EMAIL_TEMPLATE_PLACEHOLDER_GROUPS)[number];

/** The menu heading each placeholder sits under (Empfänger · Objekt · Interessent · Absender · Firma). */
export const EMAIL_TEMPLATE_PLACEHOLDER_GROUP: Readonly<
  Record<EmailTemplatePlaceholder, EmailTemplatePlaceholderGroup>
> = {
  RECIPIENT_GREETING: 'RECIPIENT',
  RECIPIENT_SALUTATION: 'RECIPIENT',
  RECIPIENT_FIRST_NAME: 'RECIPIENT',
  RECIPIENT_LAST_NAME: 'RECIPIENT',
  PROPERTY_TITLE: 'PROPERTY',
  PROPERTY_LOCATION: 'PROPERTY',
  PROPERTY_ADDRESS: 'PROPERTY',
  PROPERTY_PRICE: 'PROPERTY',
  PROPERTY_NUMBER: 'PROPERTY',
  PROSPECT_OFFER: 'PROSPECT',
  PROSPECT_NOTARY_APPOINTMENT: 'PROSPECT',
  PROSPECT_PERSONAL_LINK: 'PROSPECT',
  SENDER_FIRST_NAME: 'SENDER',
  SENDER_LAST_NAME: 'SENDER',
  SENDER_PHONE: 'SENDER',
  SENDER_EMAIL: 'SENDER',
  COMPANY_NAME: 'COMPANY',
};

export const isEmailTemplatePlaceholder = (value: string): value is EmailTemplatePlaceholder =>
  (EMAIL_TEMPLATE_PLACEHOLDERS as ReadonlyArray<string>).includes(value);

export const EMAIL_TEMPLATE_NAME_MAX = 80;
export const EMAIL_TEMPLATE_SUBJECT_VISIBLE_MAX = 200;
export const EMAIL_TEMPLATE_BODY_VISIBLE_MAX = 20_000;
export const EMAIL_TEMPLATE_FILL_FIELD_LABEL_MAX = 40;
// Markup ceilings: a DoS backstop only — the visible-text limits are domain rules.
export const EMAIL_TEMPLATE_SUBJECT_MARKUP_MAX = 20_000;
export const EMAIL_TEMPLATE_BODY_MARKUP_MAX = 400_000;

export const EmailTemplateSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  area: EmailTemplateAreaSchema,
  subject: z.string(),
  updatedAt: z.iso.datetime(),
  // null for a starter Vorlage no member has saved since (shown as „immoOS“).
  updatedByMemberId: refersTo('member', z.uuid()).nullable(),
  updatedByName: z.string().nullable(),
});
export type EmailTemplateSummary = z.infer<typeof EmailTemplateSummarySchema>;

export const EmailTemplateSchema = EmailTemplateSummarySchema.extend({
  body: z.string(),
});
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>;

export const ListEmailTemplatesResponseSchema = z.object({ data: z.array(EmailTemplateSummarySchema) });
export type ListEmailTemplatesResponse = z.infer<typeof ListEmailTemplatesResponseSchema>;

export const EmailTemplatesPathParamsSchema = z.object({ workspaceId: z.uuid() });
export const EmailTemplatePathParamsSchema = z.object({ workspaceId: z.uuid(), emailTemplateId: z.uuid() });

export const EmailTemplateNameSchema = z.string().trim().min(1).max(EMAIL_TEMPLATE_NAME_MAX);

export const CreateEmailTemplateBodySchema = z
  .object({
    name: EmailTemplateNameSchema,
    area: EmailTemplateAreaSchema,
    subject: z.string().max(EMAIL_TEMPLATE_SUBJECT_MARKUP_MAX),
    body: z.string().max(EMAIL_TEMPLATE_BODY_MARKUP_MAX),
  })
  .strict();
export type CreateEmailTemplateBody = z.infer<typeof CreateEmailTemplateBodySchema>;

// A save replaces all four (the editor shows all four; no field is fixed after creation).
export const UpdateEmailTemplateBodySchema = CreateEmailTemplateBodySchema;
export type UpdateEmailTemplateBody = z.infer<typeof UpdateEmailTemplateBodySchema>;
